import { create } from 'zustand';
import { api } from '../services/api';
import { initLanguageFromPreferences } from '../i18n';

// Post list preferences
export interface PostListPreferences {
  statusFilter: 'all' | 'draft' | 'scheduled' | 'publishing' | 'published' | 'failed' | 'cancelled';
  platformFilter: 'all' | 'facebook' | 'instagram';
  postTypeFilter: 'all' | 'feed' | 'reel' | 'story' | 'carousel';
  groupBy: 'none' | 'date' | 'week' | 'month' | 'status' | 'platform';
  expandAll: boolean;
}

// Meta overview preferences
export interface MetaOverviewPreferences {
  selectedProjectId: string;
  platformFilter: 'all' | 'instagram' | 'facebook';
  postTypeFilter: 'all' | 'feed' | 'carousel' | 'reel' | 'story';
  timeRange: 'all' | '7d' | '30d' | '90d' | '365d';
  flightFilter: string;
  groupBy: 'platform' | 'date' | 'postType' | 'flight' | 'status' | 'none';
}

// Flights page preferences
export interface FlightsPreferences {
  selectedProjectId: string;
  statusFilter: 'all' | 'draft' | 'active' | 'paused' | 'completed' | 'archived';
  groupBy: 'none' | 'status' | 'date';
  sortBy: 'name' | 'start_date' | 'end_date' | 'created_at';
  sortOrder: 'asc' | 'desc';
}

export interface UserPreferences {
  postList: PostListPreferences;
  metaOverview?: MetaOverviewPreferences;
  flights?: FlightsPreferences;
  language?: 'de' | 'it';
}

interface Tenant {
  id: string;
  tenant_id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  primary_color: string;
  city: string | null;
}

interface User {
  id: string;
  display_id: string;
  email: string;
  name: string | null;
  first_name: string | null;
  last_name: string | null;
  role: string;
  avatar_url: string | null;
  phone: string | null;
  company: string | null;
  position: string | null;
  bio: string | null;
  tenant_id: string | null;
  tenant?: Tenant | null;
  // Adressfelder
  street: string | null;
  house_number: string | null;
  postal_code: string | null;
  city: string | null;
  country: string | null;
  // URLs
  website: string | null;
  linkedin_url: string | null;
  xing_url: string | null;
  // Preferences
  preferences: UserPreferences;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name?: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  updatePreferences: (prefs: Partial<UserPreferences>) => Promise<void>;
  clearError: () => void;
}

interface AuthResponse {
  user: User;
  accessToken: string;
  // refreshToken is now sent as HTTP-only cookie, not in response body
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,

  login: async (email: string, password: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.post<AuthResponse>('/auth/login', {
        email,
        password,
      });

      // Store access token in memory (refresh token is in HTTP-only cookie)
      api.setAccessToken(response.accessToken);

      // Initialize language from user preferences
      initLanguageFromPreferences(response.user.preferences?.language);

      set({
        user: response.user,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Login failed',
        isLoading: false,
      });
      throw error;
    }
  },

  register: async (email: string, password: string, name?: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.post<AuthResponse>('/auth/register', {
        email,
        password,
        name,
      });

      // Store access token in memory (refresh token is in HTTP-only cookie)
      api.setAccessToken(response.accessToken);

      set({
        user: response.user,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Registration failed',
        isLoading: false,
      });
      throw error;
    }
  },

  logout: async () => {
    try {
      // Server will clear the HTTP-only cookie
      await api.post('/auth/logout');
    } catch {
      // Ignore logout errors
    }

    api.setAccessToken(null);

    set({
      user: null,
      isAuthenticated: false,
      isLoading: false,
    });
  },

  checkAuth: async () => {
    try {
      // Check if we have a valid session via HTTP-only cookie
      const hasSession = await api.checkSession();

      if (!hasSession) {
        set({ isLoading: false, isAuthenticated: false });
        return;
      }

      // Session is valid, fetch user data
      const response = await api.get<{ user: User }>('/auth/me');

      // Initialize language from user preferences
      initLanguageFromPreferences(response.user.preferences?.language);

      set({
        user: response.user,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch (error) {
      // Log the error for debugging
      console.error('Auth check failed:', error);

      // Try one more time after a short delay (backend might still be starting)
      try {
        await new Promise(resolve => setTimeout(resolve, 500));
        const hasSession = await api.checkSession();

        if (hasSession) {
          const response = await api.get<{ user: User }>('/auth/me');

          // Initialize language from user preferences
          initLanguageFromPreferences(response.user.preferences?.language);

          set({
            user: response.user,
            isAuthenticated: true,
            isLoading: false,
          });
          return;
        }
      } catch {
        // Retry also failed, ignore
      }

      // Clear auth state after retry failed
      api.setAccessToken(null);
      set({ isLoading: false, isAuthenticated: false });
    }
  },

  updatePreferences: async (prefs: Partial<UserPreferences>) => {
    try {
      const response = await api.patch<{ success: boolean; preferences: UserPreferences }>('/auth/preferences', prefs);

      set((state) => ({
        user: state.user ? {
          ...state.user,
          preferences: response.preferences,
        } : null,
      }));
    } catch (error) {
      console.error('Failed to update preferences:', error);
    }
  },

  clearError: () => set({ error: null }),
}));
